import { getOption } from './api.utils';
import { unsubscribe } from '@ppl/utils/subscription.utils';
import { timer } from 'rxjs';
import {
  concatMap,
  first,
  map,
  take,
  takeWhile
} from 'rxjs/operators';
import type { Observable, RetryConfig, Subscription } from 'rxjs';


const DEFAULT_POLL_PERIOD = 1000;
const DEFAULT_POLL_MAX_COUNT = 2 ** 32;

export class PollMaxCountReachedError<T> extends Error {
  constructor(public readonly lastResponse: T) {
    super('Poll max count reached');
  }
}

export function pollWhile<T>(
  observable$: Observable<T>,
  isDoneCallback: (response: T) => boolean,
  options: PollWhileOptions = {}
) {
  const pollMaxCount = getOption(options, 'pollMaxCount', DEFAULT_POLL_MAX_COUNT);

  return timer(0, getOption(options, 'pollPeriod', DEFAULT_POLL_PERIOD)).pipe(
    take(pollMaxCount),
    concatMap((index) => observable$.pipe(map(response => ({ response, index })))),
    takeWhile(({ response }) => !isDoneCallback(response), true),
    map(({ response, index }) => {
      if (isDoneCallback(response)) {
        return [response, true] as const;
      }
      if (index >= (pollMaxCount - 1)) {
        throw new PollMaxCountReachedError(response);
      }
      return [response, false] as const;
    })
  );
}

interface PollWhileOptions {
  pollPeriod?: number;
  pollMaxCount?: number;
}

const DEFAULT_SCALING_DURATION = 1000;
const DEFAULT_MAX_RETRY_ATTEMPTS = 3;

export function defaultRetryStrategy(options: RetryStrategyOptions = {}): RetryConfig {
  const maxRetryAttempts = getOption(options, 'maxRetryAttempts', DEFAULT_MAX_RETRY_ATTEMPTS);
  const scalingDuration = getOption(options, 'scalingDuration', DEFAULT_SCALING_DURATION);

  return {
    count: maxRetryAttempts,
    delay: (error, retryCount) => timer(retryCount * scalingDuration)
  };
}

interface RetryStrategyOptions {
  scalingDuration?: number;
  maxRetryAttempts?: number;
}

export interface PollingHandle {
  stop: () => void;
  start: () => void;
}

interface CreatePollingCommonInfo {
  numOfFailedAttempts: number;
  pollCount: number;
}
export type CreatePollingErrorInfo = CreatePollingCommonInfo;
export type CreatePollingErrorFn = (error: any, errorInfo: CreatePollingErrorInfo) => boolean;

export type CreatePollingDoneInfo = CreatePollingCommonInfo;
export type CreatePollingDoneFn<T> = (result: T, info: CreatePollingDoneInfo) => void;

export type CreatePollingStopInfo = CreatePollingCommonInfo;
export type CreatePollingStopFn<T> = (result: T, info: CreatePollingStopInfo) => void;

export interface CreatePollingOptions {
  pollInterval: number;
  pollMaxCount: number;
}

export function createPolling<T>(
  checkFn: () => Observable<T>,
  isDoneFn: (result: T) => boolean,
  callbacks: {
    onDoneFn: CreatePollingDoneFn<T>,
    onErrorFn: CreatePollingErrorFn,
    onStopFn?: CreatePollingStopFn<T>,
  },
  options: CreatePollingOptions = { pollInterval: DEFAULT_POLL_PERIOD, pollMaxCount: DEFAULT_POLL_MAX_COUNT }
): PollingHandle {
  let timeoutId: ReturnType<typeof setTimeout>;
  let subscription: Subscription;
  let numOfFailedAttempts = 0;
  let pollCount = 0;

  const scheduleNextCheck = () => {
    timeoutId = setTimeout(startPolling, options.pollInterval);
  };

  const stopPolling = () => {
    clearTimeout(timeoutId);
    unsubscribe(subscription);
  };

  const startPolling = () => {
    stopPolling();
    pollCount++;
    subscription = checkFn().pipe(first()).subscribe({
      next: result => {
        stopPolling();
        numOfFailedAttempts = 0;

        if (isDoneFn(result)) {
          callbacks.onDoneFn(result, { numOfFailedAttempts, pollCount });
        } else if (pollCount >= options.pollMaxCount) {
          callbacks.onStopFn?.(result, { numOfFailedAttempts, pollCount });
        } else {
          scheduleNextCheck();
        }
      },
      error: error => {
        stopPolling();
        numOfFailedAttempts += 1;

        const tryAgain = callbacks.onErrorFn(error, { numOfFailedAttempts, pollCount });
        if (tryAgain) {
          scheduleNextCheck();
        }
      }
    });
  };

  return {
    stop: stopPolling,
    start: startPolling
  };
}
